import { useState, type ChangeEvent } from "react";

export type ValidationRule<T> = {
  pattern?: T[keyof T] extends string ? RegExp : never;
  custom?: (value: T[keyof T], values: FormValues<T>) => string | undefined;
  message?: string;
  required?: boolean;
  conditional?: (values: FormValues<T>) => boolean;
};

export type ValidationRules<T> = {
  [key in keyof T]: ValidationRule<T>;
};

export type FormValues<T> = {
  [key in keyof T]: T[key];
};

export type FormErrors<T> = {
  [key in keyof T]?: {
    pattern?: string;
    custom?: string;
    required?: string;
  };
};

export type FormValidator<T> = {
  values: FormValues<T>;
  errors: FormErrors<T>;
  handleChange: (
    event: ChangeEvent<HTMLElement>,
    fieldName: keyof T,
    validate?: boolean
  ) => void;
  validateForm: () => boolean;
  initializeForm: (initialState: FormValues<T>) => void;
  validateField: (fieldName: keyof T, fieldValue?: T[keyof T]) => boolean;
  setFieldValue: (
    fieldName: keyof T,
    value: T[keyof T],
    validate?: boolean
  ) => void;
};

const useFormValidator = <T>(
  initialState: FormValues<T>,
  validationRules: ValidationRules<T>
): FormValidator<T> => {
  const [values, setValues] = useState<FormValues<T>>(initialState);
  const [errors, setErrors] = useState<FormErrors<T>>({});

  const validate = (
    fieldName: keyof T,
    fieldValue: T[keyof T]
  ): FormErrors<T> => {
    const validationRule = validationRules[fieldName] ?? {};
    if (validationRule.conditional && !validationRule.conditional(values)) {
      return {};
    }

    const newErrors: FormErrors<T> = {};

    if (validationRule.required && !fieldValue) {
      newErrors[fieldName] = {
        ...newErrors[fieldName],
        required: validationRule.message || `This field is required`,
      };
    }

    if (
      validationRule.pattern &&
      fieldValue &&
      typeof fieldValue === "string" &&
      !validationRule.pattern.test(fieldValue)
    ) {
      newErrors[fieldName] = {
        ...newErrors[fieldName],
        pattern: validationRule.message || `${String(fieldName)} is invalid`,
      };
    }

    if (validationRule.custom) {
      const customError = validationRule.custom(fieldValue, values);
      if (customError) {
        newErrors[fieldName] = {
          ...newErrors[fieldName],
          custom: customError || `${String(fieldName)} is invalid`,
        };
      }
    }

    return newErrors;
  };
  const validateForm = (): boolean => {
    const newErrors: FormErrors<T> = {};

    Object.keys(validationRules).forEach((f) => {
      const fieldName = f as keyof T;
      const fieldValue = values[fieldName as keyof T];
      const fieldErrors = validate(fieldName, fieldValue);
      if (Object.keys(fieldErrors).length > 0) {
        newErrors[fieldName] = fieldErrors[fieldName];
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    event: ChangeEvent<HTMLElement>,
    fieldName: keyof T,
    _validate?: boolean
  ): void => {
    const { value, type, checked } = event.target as HTMLInputElement;
    const isCheckbox = type === "checkbox";
    const inputValue = isCheckbox ? checked.toString() : value;
    setValues((prevValues) => ({
      ...prevValues,
      [fieldName]: inputValue,
    }));
    if (_validate) {
      const fieldKey = fieldName as keyof T;
      const typedInputValue = inputValue as unknown as T[keyof T];
      validateField(fieldKey, typedInputValue);
    }
  };

  const setFieldValue = (
    fieldName: keyof T,
    value: T[keyof T],
    _validate?: boolean
  ): void => {
    setValues((prevValues) => ({
      ...prevValues,
      [fieldName]: value,
    }));
    if (_validate) {
      validateField(fieldName, value);
    }
  };

  const validateField = (
    fieldName: keyof T,
    fieldValue?: T[keyof T]
  ): boolean => {
    const _fieldValue = fieldValue ?? values[fieldName];
    const _errors = validate(fieldName, _fieldValue);

    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors } as FormErrors<T>;
      if (_errors[fieldName] !== undefined) {
        newErrors[fieldName] = _errors[fieldName];
      } else {
        delete newErrors[fieldName];
      }
      return newErrors;
    });

    return !_errors[fieldName];
  };

  const initializeForm = (_initialState: FormValues<T>): void => {
    setValues(_initialState);
    setErrors({});
  };

  return {
    values,
    errors,
    handleChange,
    setFieldValue,
    validateForm,
    initializeForm,
    validateField,
  };
};

export default useFormValidator;
